import type { Database } from '@/integrations/supabase/types';

export type PayerNormalizedStatus = Database['public']['Enums']['payer_normalized_status'];

// Mapping rules for raw status strings to normalized statuses
const STATUS_PATTERNS: Array<{ pattern: RegExp; normalized: PayerNormalizedStatus }> = [
  // SUBMITTED patterns
  { pattern: /^(submitted|received|pending\s*review|awaiting|in\s*queue)/i, normalized: 'SUBMITTED' },
  
  // IN_REVIEW patterns
  { pattern: /(in\s*review|under\s*review|processing|reviewing|being\s*reviewed|clinical\s*review)/i, normalized: 'IN_REVIEW' },
  
  // NEED_MORE_INFO patterns
  { pattern: /(need.*info|additional\s*info|more\s*info|pend.*info|request.*doc|missing.*doc|insufficient)/i, normalized: 'NEED_MORE_INFO' },
  
  // APPROVED patterns
  { pattern: /(approved|authorized|auth\s*granted|certified|accepted)/i, normalized: 'APPROVED' },
  
  // DENIED patterns
  { pattern: /(denied|rejected|not\s*approved|declined|adverse|unfavorable)/i, normalized: 'DENIED' },
  
  // WITHDRAWN patterns
  { pattern: /(withdrawn|cancelled|canceled|voided|retracted)/i, normalized: 'WITHDRAWN' },
  
  // CLOSED patterns
  { pattern: /(closed|complete|finalized|resolved|expired)/i, normalized: 'CLOSED' },
];

export function normalizePayerStatus(rawStatus: string | null | undefined): PayerNormalizedStatus | null {
  if (!rawStatus) return null;
  
  const trimmed = rawStatus.trim().toLowerCase();
  
  for (const { pattern, normalized } of STATUS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return normalized;
    }
  }
  
  // Default to SUBMITTED if we can't classify
  return 'SUBMITTED';
}

export const NORMALIZED_STATUS_LABELS: Record<PayerNormalizedStatus, string> = {
  SUBMITTED: 'Submitted',
  IN_REVIEW: 'In Review',
  NEED_MORE_INFO: 'More Info Needed',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  WITHDRAWN: 'Withdrawn',
  CLOSED: 'Closed',
};

export const NORMALIZED_STATUS_COLORS: Record<PayerNormalizedStatus, string> = {
  SUBMITTED: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  IN_REVIEW: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  NEED_MORE_INFO: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  APPROVED: 'bg-primary/10 text-primary border-primary/30',
  DENIED: 'bg-red-500/10 text-red-500 border-red-500/30',
  WITHDRAWN: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  CLOSED: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

export const SOURCE_LABELS: Record<string, string> = {
  portal: 'Payer Portal',
  fhir: 'FHIR/ePAS',
  fax: 'Fax Response',
  phone: 'Phone Call',
  manual: 'Manual Entry',
};
